import * as React from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import type { FieldFormProps } from '@helpcenter/field-types';
import type { showErrorFlag } from '@helpcenter/flags';
import { FormMultiSelect } from '@helpcenter/form-select';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { MultiServicePickerOption } from 'rest/service-picker';
import type { ValueType } from '@atlaskit/select/types';
import { toMultiSelectValue } from '@atlassian/help-center-common-util/select';
import messages from './messages';
import { usePaginatedServiceOptions } from './use-paginated-service-options';

const MultiServicePicker = (
	props: FieldFormProps<string[]> &
		WrappedComponentProps & {
			showErrorFlag: typeof showErrorFlag;
			defaultServices?: string[];
			value?: string[];
		},
) => {
	const { disabled, onChange, intl, defaultServices, value, ...otherProps } = props;
	const { useCallback, useEffect, useRef, useState } = React;

	const noOptionsMessage = useCallback(() => {
		return intl.formatMessage(messages.noOptions);
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { options, fetchMoreOptions, ...paginatedMultiSelectProps } = usePaginatedServiceOptions(
		props.showErrorFlag,
	);
	const [hasAppliedDefaults, setHasAppliedDefaults] = useState(false);

	const optionsCacheRef = useRef<Map<string, MultiServicePickerOption>>(new Map());

	// Keep the cache up-to-date whenever a new page of options arrives.
	useEffect(() => {
		if (isJsmPortalRovoIntegrationEnabled()) {
			options.forEach((option) => {
				optionsCacheRef.current.set(option.value, option);
			});
		}
	}, [options]);

	const onChangeForMultiSelect = useCallback(
		(opts: ValueType<MultiServicePickerOption>) => {
			const multiSelectValue = toMultiSelectValue(opts);
			onChange?.(
				props.id,
				multiSelectValue.map((v) => v.value),
			);

			// search again if all the options have been selected
			if (multiSelectValue.length === options.length) {
				fetchMoreOptions();
			}
		},
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[onChange, options.length],
	);

	// When the Rovo integration flag is on, services are loaded, and defaultServices is provided,
	// select them as an initial pre-fill. This only runs once (guarded by hasAppliedDefaults).
	useEffect(() => {
		if (
			isJsmPortalRovoIntegrationEnabled() &&
			defaultServices &&
			defaultServices.length > 0 &&
			options.length > 0 &&
			!hasAppliedDefaults
		) {
			const selectedOptions = options.filter(
				(option) =>
					defaultServices.includes(option.value) || defaultServices.includes(option.label),
			);
			if (selectedOptions.length > 0) {
				setHasAppliedDefaults(true);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				onChangeForMultiSelect(selectedOptions as any);
			}
		}
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultServices, options.length, onChangeForMultiSelect, hasAppliedDefaults]);

	const displayValue =
		isJsmPortalRovoIntegrationEnabled() && value && Array.isArray(value) && value.length > 0
			? value.reduce<MultiServicePickerOption[]>((acc, id) => {
					const cached = optionsCacheRef.current.get(id);
					if (cached) {
						acc.push(cached);
					}
					return acc;
				}, [])
			: undefined;

	return (
		<FormMultiSelect
			{...otherProps}
			{...paginatedMultiSelectProps}
			actionSubjectId="multiServicePickerField"
			placeholder={intl.formatMessage(messages.searchPlaceholder)}
			noOptionsMessage={noOptionsMessage}
			isDisabled={disabled}
			onMenuScrollToBottom={fetchMoreOptions}
			options={options}
			onChange={onChangeForMultiSelect}
			value={displayValue}
		/>
	);
};

const WithErrorBoundary = (
	props: FieldFormProps<string[]> &
		WrappedComponentProps & {
			showErrorFlag: typeof showErrorFlag;
			defaultServices?: string[];
			value?: string[];
		},
) => (
	<ErrorBoundary id="multi-service-picker" packageName="portal_fields">
		<MultiServicePicker {...props} />
	</ErrorBoundary>
);

export default injectIntl(WithErrorBoundary);
